import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type {
  ConsolidatedBalanceSheetResponse,
  ConsolidatedIncomeStatementResponse,
  ConsolidationEntryRecord,
  ConsolidationFinancialsResponse,
  ConsolidationGroup,
  CreateConsolidationEntryInput,
  CreateConsolidationGroupInput,
  NciResponse,
  UpdateConsolidationEntryInput,
} from '@dcc-erp/shared';

import { api, apiJson } from '../api';

// Group-scoped endpoints — no companyId in the path.
const baseKey = ['consolidation'] as const;

// ===== Groups =====

export function useConsolidationGroups() {
  return useQuery({
    queryKey: [...baseKey, 'groups'] as const,
    queryFn: () => api<ConsolidationGroup[]>(`/consolidation/groups`),
  });
}

export function useConsolidationGroup(groupId: string | undefined) {
  return useQuery({
    queryKey: [...baseKey, 'group', groupId] as const,
    queryFn: () => api<ConsolidationGroup>(`/consolidation/groups/${groupId}`),
    enabled: !!groupId,
  });
}

export function useCreateConsolidationGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateConsolidationGroupInput) =>
      apiJson<CreateConsolidationGroupInput, ConsolidationGroup>(
        'POST',
        `/consolidation/groups`,
        input,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...baseKey, 'groups'] }),
  });
}

// ===== Read-side aggregates =====

export function useConsolidationFinancials(
  groupId: string | undefined,
  period: string | undefined,
) {
  return useQuery({
    queryKey: [...baseKey, 'financials', groupId, period] as const,
    queryFn: () =>
      api<ConsolidationFinancialsResponse>(
        `/consolidation/groups/${groupId}/financials?period=${period}`,
      ),
    enabled: !!groupId && !!period,
  });
}

export function useConsolidatedBalanceSheet(
  groupId: string | undefined,
  asOfDate: string | undefined,
) {
  return useQuery({
    queryKey: [...baseKey, 'balance-sheet', groupId, asOfDate] as const,
    queryFn: () => {
      const qs = asOfDate ? `?asOfDate=${asOfDate}` : '';
      return api<ConsolidatedBalanceSheetResponse>(
        `/consolidation/groups/${groupId}/balance-sheet${qs}`,
      );
    },
    enabled: !!groupId,
  });
}

export function useConsolidatedIncomeStatement(
  groupId: string | undefined,
  dateFrom: string | undefined,
  dateTo: string | undefined,
) {
  return useQuery({
    queryKey: [...baseKey, 'income-statement', groupId, dateFrom, dateTo] as const,
    queryFn: () =>
      api<ConsolidatedIncomeStatementResponse>(
        `/consolidation/groups/${groupId}/income-statement?dateFrom=${dateFrom}&dateTo=${dateTo}`,
      ),
    enabled: !!groupId && !!dateFrom && !!dateTo,
  });
}

export function useConsolidatedNci(groupId: string | undefined, asOfDate: string | undefined) {
  return useQuery({
    queryKey: [...baseKey, 'nci', groupId, asOfDate] as const,
    queryFn: () => {
      const qs = asOfDate ? `?asOfDate=${asOfDate}` : '';
      return api<NciResponse>(`/consolidation/groups/${groupId}/nci${qs}`);
    },
    enabled: !!groupId,
  });
}

// ===== Eliminations =====

export function useConsolidationEliminations(
  groupId: string | undefined,
  period: string | undefined,
) {
  return useQuery({
    queryKey: [...baseKey, 'eliminations', groupId, period] as const,
    queryFn: () => {
      const qs = period ? `?period=${period}` : '';
      return api<ConsolidationEntryRecord[]>(`/consolidation/groups/${groupId}/eliminations${qs}`);
    },
    enabled: !!groupId,
  });
}

export function useCreateElimination(groupId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateConsolidationEntryInput) =>
      apiJson<CreateConsolidationEntryInput, ConsolidationEntryRecord>(
        'POST',
        `/consolidation/groups/${groupId}/eliminations`,
        input,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [...baseKey, 'eliminations', groupId] });
      void qc.invalidateQueries({ queryKey: [...baseKey, 'balance-sheet', groupId] });
      void qc.invalidateQueries({ queryKey: [...baseKey, 'income-statement', groupId] });
    },
  });
}

export function useUpdateElimination(groupId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ eid, body }: { eid: string; body: UpdateConsolidationEntryInput }) =>
      apiJson<UpdateConsolidationEntryInput, ConsolidationEntryRecord>(
        'PATCH',
        `/consolidation/groups/${groupId}/eliminations/${eid}`,
        body,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...baseKey, 'eliminations', groupId] }),
  });
}

export function usePostElimination(groupId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (eid: string) =>
      api<ConsolidationEntryRecord>(`/consolidation/groups/${groupId}/eliminations/${eid}/post`, {
        method: 'POST',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [...baseKey, 'eliminations', groupId] });
      void qc.invalidateQueries({ queryKey: [...baseKey, 'balance-sheet', groupId] });
      void qc.invalidateQueries({ queryKey: [...baseKey, 'income-statement', groupId] });
    },
  });
}
